import axios from 'axios'
import { encerrarSessao, gravarSessao, lerSessao } from './armazenamento'
import type { Sessao } from '../types/gmud'

/**
 * Uma renovação por vez.
 *
 * O token de acesso vale 15 minutos e a tela de detalhe dispara três consultas juntas: sem
 * esta trava, as três levam 401 ao mesmo tempo e saem três POSTs de renovação em paralelo.
 * O backend aguenta (o token de renovação é um JWT sem estado, verificado só por assinatura,
 * validade e tipo em `TokenDeSessaoService.validar` — não é de uso único), então o preço
 * não é perder a sessão: são duas idas ao servidor jogadas fora e uma corrida em que a
 * última resposta a chegar sobrescreve a sessão gravada pelas outras. Guardar a promessa em
 * curso resolve os dois, e continua valendo se um dia o token de renovação passar a ser
 * revogado a cada uso.
 */
let renovacaoEmCurso: Promise<Sessao | null> | null = null

/**
 * Troca o token de renovação por uma sessão nova.
 *
 * Usa `axios` puro, e não a instância `api`: passar pelo interceptor faria um 401 na
 * renovação disparar outra renovação, em laço.
 *
 * @returns a sessão renovada, ou nulo quando não foi possível renovar
 */
export function renovarSessao(): Promise<Sessao | null> {
  if (renovacaoEmCurso) {
    return renovacaoEmCurso
  }

  const refreshToken = lerSessao()?.refreshToken
  if (!refreshToken) {
    // Quem chegou aqui tinha um token de acesso que levou 401 — e não tem com o que renovar.
    // É a aba que ficou para trás depois de um logout em outra aba (ou de um storage limpo):
    // sem o encerramento explícito, ela ficava presa em telas de erro aparentando logada.
    encerrarSessao()
    return Promise.resolve(null)
  }

  renovacaoEmCurso = axios
    .post<Sessao>('/api/v1/auth/refresh', { refreshToken }, { headers: { Accept: 'application/json' } })
    .then((res) => {
      if (!sessaoValida(res.data)) {
        // Um proxy ou portal cativo que responda 200 com HTML no lugar do backend não pode
        // virar sessão gravada — o login por senha já se protege assim (`ehSessao`); a
        // renovação recebia o mesmo corpo e gravava sem olhar. Corpo estranho é falha de
        // renovação, não fim de sessão: a próxima tentativa renova de verdade.
        return null
      }
      gravarSessao(res.data)
      return res.data
    })
    .catch((erro: unknown) => {
      if (sessaoRecusada(erro)) {
        // O servidor disse que este token não vale mais. Encerrar aqui evita que a
        // tela fique tentando chamadas que nunca vão passar.
        encerrarSessao()
      }
      return null
    })
    .finally(() => {
      renovacaoEmCurso = null
    })

  return renovacaoEmCurso
}

/**
 * Só o servidor recusa uma sessão.
 *
 * Queda de rede, 502 do proxy durante um deploy e timeout devolvem exatamente o mesmo
 * `catch` que um token vencido — e tratar os quatro como "acabou a sessão" desloga quem
 * está no meio de uma aprovação do comitê por causa de meio segundo de instabilidade, com
 * o formulário aberto e sem aviso nenhum. Sem resposta do servidor, a sessão continua de
 * pé: a chamada original falha com "sem conexão", que é o que de fato aconteceu, e a
 * próxima tentativa renova.
 *
 * @param erro o que a promessa da renovação rejeitou
 * @returns verdadeiro só quando o servidor respondeu recusando a credencial
 */
function sessaoRecusada(erro: unknown): boolean {
  const status = axios.isAxiosError(erro) ? erro.response?.status : undefined
  return status === 401 || status === 403
}

/**
 * O corpo do refresh serve como sessão? Os dois tokens são obrigatórios — meia sessão gravada
 * é um logout silencioso 15 minutos depois, sem chance de renovar.
 *
 * @param corpo o que o servidor respondeu
 * @returns verdadeiro quando dá para gravar
 */
function sessaoValida(corpo: unknown): corpo is Sessao {
  const sessao = corpo as Partial<Sessao> | null
  return (
    typeof sessao?.accessToken === 'string' &&
    sessao.accessToken !== '' &&
    typeof sessao.refreshToken === 'string' &&
    sessao.refreshToken !== ''
  )
}
